import React, { useCallback, useEffect, useState } from 'react';
import {
  Row, Col, Card, Statistic, Typography, Button, Alert, Skeleton, Empty, Space, Tag,
} from 'antd';
import {
  TeamOutlined, UserDeleteOutlined, FileTextOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { adminApi } from '../services/api';

const { Title, Text } = Typography;

const COLORI_PIANO: Record<string, string> = {
  trial: '#C67C05', base: '#4F72FF', medium: '#9B5CF6', premium: '#0EA26B',
};

interface DashboardData {
  total_users: number;
  active_users: number;
  churned_users: number;
  mrr: number;
  monthly_revenue: number;
  users_by_plan: Record<string, number>;
  new_users_per_month: Record<string, number>;
  total_posts: number;
  published_posts: number;
  failed_posts: number;
}

const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const res = await adminApi.dashboard();
      setData(res.data);
    } catch {
      setErrore('Non è stato possibile caricare i dati.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Scheletro della pagina invece di un "Caricamento..." nudo: si vede
  // subito la forma di cio che sta arrivando e la pagina non salta.
  if (loading) {
    return (
      <>
        <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
          {[0, 1, 2, 3].map(i => (
            <Col xs={12} sm={12} xl={6} key={i}>
              <Card><Skeleton active paragraph={{ rows: 1 }} /></Card>
            </Col>
          ))}
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}><Card><Skeleton active paragraph={{ rows: 6 }} /></Card></Col>
          <Col xs={24} lg={10}><Card><Skeleton active paragraph={{ rows: 6 }} /></Card></Col>
        </Row>
      </>
    );
  }

  if (errore || !data) {
    return (
      <Alert
        type="error" showIcon
        message="Caricamento non riuscito"
        description={errore || 'Nessun dato disponibile.'}
        action={<Button onClick={load}>Riprova</Button>}
      />
    );
  }

  const perMese = Object.entries(data.new_users_per_month).map(([m, n]) => ({
    mese: MESI[parseInt(m.slice(5), 10) - 1] || m.slice(5),
    utenti: n,
  }));

  const perPiano = Object.entries(data.users_by_plan)
    .filter(([, n]) => n > 0)
    .map(([p, n]) => ({
      name: p.charAt(0).toUpperCase() + p.slice(1),
      value: n,
      color: COLORI_PIANO[p] || '#98A1B8',
    }));

  const euro = (n: number) => `€ ${n.toLocaleString('it-IT')}`;

  return (
    <>
      <div className="page-top">
        <div>
          <Title level={3} style={{ margin: 0 }}>Dashboard</Title>
          <Text type="secondary">Il quadro generale del servizio</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Aggiorna</Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
        <Col xs={12} sm={12} xl={6}>
          <Card>
            <Statistic
              title="Ricavi ricorrenti"
              value={data.mrr}
              formatter={v => euro(Number(v))}
              valueStyle={{ color: '#0EA26B' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>al mese, dagli abbonamenti attivi</Text>
          </Card>
        </Col>
        <Col xs={12} sm={12} xl={6}>
          <Card>
            <Statistic
              title="Utenti"
              value={data.total_users}
              prefix={<TeamOutlined style={{ color: '#4F72FF' }} />}
            />
            <Tag color="success">{data.active_users} attivi</Tag>
          </Card>
        </Col>
        <Col xs={12} sm={12} xl={6}>
          <Card>
            <Statistic
              title="Abbandoni"
              value={data.churned_users}
              prefix={<UserDeleteOutlined style={{ color: '#DC3055' }} />}
              valueStyle={data.churned_users > 0 ? { color: '#DC3055' } : undefined}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>utenti che hanno lasciato</Text>
          </Card>
        </Col>
        <Col xs={12} sm={12} xl={6}>
          <Card>
            <Statistic
              title="Post"
              value={data.total_posts}
              prefix={<FileTextOutlined style={{ color: '#9B5CF6' }} />}
            />
            <Space size={6} wrap>
              <Tag color="success">{data.published_posts} pubblicati</Tag>
              {data.failed_posts > 0 && <Tag color="error">{data.failed_posts} falliti</Tag>}
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Nuovi utenti per mese">
            {perMese.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ancora nessun dato" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={perMese} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFF1F7" vertical={false} />
                  <XAxis dataKey="mese" tickLine={false} axisLine={false}
                         tick={{ fill: '#6B7590', fontSize: 12 }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false}
                         tick={{ fill: '#6B7590', fontSize: 12 }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(79,114,255,.07)' }}
                    contentStyle={{ borderRadius: 10, border: '1px solid #E4E7F0', fontSize: 13 }}
                  />
                  <Bar dataKey="utenti" fill="#4F72FF" radius={[7, 7, 0, 0]} maxBarSize={54} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Distribuzione per piano">
            {perPiano.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun abbonamento attivo" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={perPiano} dataKey="value" nameKey="name"
                    innerRadius={62} outerRadius={100} paddingAngle={3}
                  >
                    {perPiano.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #E4E7F0', fontSize: 13 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
